import React from 'react';

import { Checkbox, CheckboxProps } from '../Checkbox';
import { Fieldset } from '../Fieldset/Fieldset';
import { InlineError } from '../InlineError';
import { Radio, RadioProps } from '../Radio';

type Choice = {
  label?: string | JSX.Element;
  value: string;
};

export type ChoiceGroupProps = {
  label?: string | JSX.Element;
  id?: string;
  name: string;
  value?: string | string[];
  choices: Choice[];
  multiple?: boolean;
  error?: string | JSX.Element;
  onChange?(
    event: React.FormEvent<HTMLInputElement>,
    newValue: string | string[] | undefined,
    name: string,
  ): void;
  onBlur?(event: React.FocusEvent): void;
  disabled?: boolean;
};

type SharedInputProps =
  | Pick<RadioProps, 'radioGroup' | 'onChange' | 'onBlur'>
  | Pick<CheckboxProps, 'onChange' | 'onBlur'>;

const isSelected = (
  value: string,
  selectedValueOrValues: string | string[] = [],
) => {
  const selected = Array.isArray(selectedValueOrValues)
    ? selectedValueOrValues
    : [selectedValueOrValues];

  return selected.includes(value);
};

const calculateValue = (
  allowMultiple: boolean,
  value: string,
  checked: boolean,
  selected?: string | string[],
): string | string[] | undefined => {
  if (!allowMultiple) {
    return value;
  }

  if (Array.isArray(selected)) {
    if (checked) {
      return [...selected, value];
    }
    return selected.filter(selectedChoice => selectedChoice !== value);
  }

  if (checked) {
    return [value];
  }

  return undefined;
};

export const ChoiceGroup: React.FunctionComponent<ChoiceGroupProps> = ({
  choices = [],
  multiple = false,
  name,
  label,
  error,
  value: selected,
  disabled,
  ...props
}) => {
  const onChange = (
    event: React.FormEvent<HTMLInputElement>,
    newChecked: boolean,
  ) => {
    const value = event.currentTarget.value;
    const newValue = calculateValue(multiple, value, newChecked, selected);

    if (props.onChange) props.onChange(event, newValue, name);
  };

  const onBlur = () => {
    if (props.onBlur) props.onBlur({ target: { name } } as any);
  };

  let Component: React.ReactType = Checkbox;
  const inputProps: SharedInputProps = { onChange, onBlur };

  if (!multiple) {
    Component = Radio;
    (inputProps as any).radioGroup = name;
  }

  return (
    <Fieldset legend={label}>
      {error ? (
        <InlineError
          className="jg-space-mbms jg-space-mbmd@md"
          message={error}
        />
      ) : null}
      {choices.map(({ label, value }) => {
        const keyOrId = `${props.id || name}-${value}`;
        const checked = isSelected(value, selected);
        return (
          <Component
            id={keyOrId}
            name={keyOrId}
            label={label}
            key={`${keyOrId}-${checked}`}
            value={value}
            checked={checked}
            disabled={disabled}
            {...inputProps}
          />
        );
      })}
    </Fieldset>
  );
};
